// src/pages/settings/settings/CompanyEmailConfiguration.tsx
import React, { useState, useEffect } from "react";
import { Button, Input, InputNumber, Select, Space, Tooltip, Modal, Divider, Typography, Alert, Form } from "antd";
import { PlusOutlined, SaveOutlined, MailOutlined, EditOutlined, DeleteOutlined, CheckOutlined } from "@ant-design/icons";
import { DataTable, SectionCard, StatusTag } from "../../../components/shared";
import DetailDrawer from "../../../components/shared/DetailDrawer";

import { companyEmailConfigApi } from "../../../api/communications/emailConfigApi";
import { validateEmailConfigForm, getConfigStatus, applyProviderPreset, sortConfigsByStatus } from "../../../utils/emailConfigUtils";
import { CompanyEmailConfig, SMTP_PROVIDERS, SMTPProvider } from "../../../types/communications/emailConfig";
import { useNotifications } from "../../../context/NotificationContext";
import { extractValidationErrors } from "../../../utils/apiError";
import { isStepUpDeclined } from "../../../lib/http/authErrors";

const { Option } = Select;
const { Text } = Typography;
const { confirm } = Modal;

interface CompanyEmailConfigurationProps {
	companyId: number;
}

const CompanyEmailConfiguration: React.FC<CompanyEmailConfigurationProps> = ({ companyId }) => {
	const [configs, setConfigs] = useState<CompanyEmailConfig[]>([]);
	const [loading, setLoading] = useState(false);
	const [drawerVisible, setDrawerVisible] = useState(false);
	const [editingConfig, setEditingConfig] = useState<CompanyEmailConfig | null>(null);
	const [testDrawerVisible, setTestDrawerVisible] = useState(false);
	const [testingConfig, setTestingConfig] = useState<CompanyEmailConfig | null>(null);
	const [testEmail, setTestEmail] = useState("");
	const [testLoading, setTestLoading] = useState(false);

	const [form] = Form.useForm();
	const { showSuccess, showError } = useNotifications();

	useEffect(() => {
		loadConfigurations();
	}, [companyId]);

	const loadConfigurations = async () => {
		setLoading(true);
		try {
			const data = await companyEmailConfigApi.getConfigurations(companyId);
			const companyConfigs = data.filter((config): config is CompanyEmailConfig => "company_id" in config);
			setConfigs(sortConfigsByStatus(companyConfigs) as CompanyEmailConfig[]);
		} catch (error) {
			showError("Failed to load email configurations");
		} finally {
			setLoading(false);
		}
	};

	const openDrawer = (config?: CompanyEmailConfig) => {
		if (config) {
			setEditingConfig(config);
			form.setFieldsValue({
				name: config.name,
				host: config.host,
				port: config.port,
				encryption: config.encryption,
				username: config.username,
				password: "",
				from_address: config.from_address,
				from_name: config.from_name,
			});
		} else {
			setEditingConfig(null);
			form.resetFields();
		}
		setDrawerVisible(true);
	};

	const closeDrawer = () => {
		setDrawerVisible(false);
		setEditingConfig(null);
		form.resetFields();
	};

	const handleProviderSelect = (provider: SMTPProvider) => {
		const updatedFormData = applyProviderPreset(provider, form.getFieldsValue());
		form.setFieldsValue(updatedFormData);
	};

	const handleSubmit = async () => {
		try {
			const values = await form.validateFields();
			const validation = validateEmailConfigForm(values);

			if (!validation.isValid) {
				Object.entries(validation.errors).forEach(([field, error]) => {
					form.setFields([{ name: field, errors: [error] }]);
				});
				return;
			}

			setLoading(true);

			if (editingConfig) {
				await companyEmailConfigApi.updateConfiguration(editingConfig.id, values);
				showSuccess("Email configuration updated successfully");
			} else {
				await companyEmailConfigApi.createConfiguration({
					...values,
					company_id: companyId,
				});
				showSuccess("Email configuration created successfully");
			}

			await loadConfigurations();
			closeDrawer();
		} catch (error) {
			// Clinic SMTP writes sit behind step-up; a dismissed prompt is the user's choice, not an error.
			if (!isStepUpDeclined(error)) showError(extractValidationErrors(error).join(" "));
		} finally {
			setLoading(false);
		}
	};

	const handleDelete = (config: CompanyEmailConfig) => {
		confirm({
			title: "Confirm Deletion",
			content: `Are you sure you want to delete the configuration "${config.name}"?`,
			okText: "Delete",
			okType: "danger",
			cancelText: "Cancel",
			onOk: async () => {
				try {
					await companyEmailConfigApi.deleteConfiguration(config.id);
					showSuccess("Configuration deleted successfully");
					await loadConfigurations();
				} catch (error) {
					if (!isStepUpDeclined(error)) showError("Failed to delete configuration");
				}
			},
		});
	};

	const handleActivate = async (config: CompanyEmailConfig) => {
		try {
			await companyEmailConfigApi.activateConfiguration(config.id);
			showSuccess("Configuration activated successfully");
			await loadConfigurations();
		} catch (error) {
			if (!isStepUpDeclined(error)) showError("Failed to activate configuration");
		}
	};

	const handleTest = (config: CompanyEmailConfig) => {
		setTestingConfig(config);
		setTestEmail("");
		setTestDrawerVisible(true);
	};

	const executeTest = async () => {
		if (!testingConfig || !testEmail) return;

		setTestLoading(true);
		try {
			const result = await companyEmailConfigApi.testConfiguration(testingConfig.id, {
				test_email: testEmail,
			});

			if (result.success) {
				showSuccess("Test email sent successfully!");
			} else {
				showError(result.message || "Test failed");
			}
			setTestDrawerVisible(false);
		} catch (error) {
			if (!isStepUpDeclined(error)) showError(extractValidationErrors(error).join(" "));
		} finally {
			setTestLoading(false);
		}
	};

	const columns = [
		{ title: "Name", dataIndex: "name", key: "name" },
		{ title: "SMTP Host", dataIndex: "host", key: "host" },
		{ title: "Port", dataIndex: "port", key: "port", width: 80 },
		{
			title: "Encryption",
			dataIndex: "encryption",
			key: "encryption",
			width: 100,
			render: (text: string) => text.toUpperCase(),
		},
		{ title: "From Address", dataIndex: "from_address", key: "from_address" },
		{
			title: "Status",
			key: "status",
			width: 130,
			render: (_: any, config: CompanyEmailConfig) => {
				const { status, statusText } = getConfigStatus(config);
				return <StatusTag status={status} label={statusText} />;
			},
		},
		{
			title: "",
			key: "actions",
			width: 110,
			render: (_: any, config: CompanyEmailConfig) => {
				const { status } = getConfigStatus(config);
				return (
					<Space size={2}>
						<Tooltip title="Test Configuration">
							<Button type="text" icon={<MailOutlined />} size="small" disabled={!config.is_verified} onClick={(e) => { e.stopPropagation(); handleTest(config); }} />
						</Tooltip>
						<Tooltip title="Edit">
							<Button type="text" icon={<EditOutlined />} size="small" onClick={(e) => { e.stopPropagation(); openDrawer(config); }} />
						</Tooltip>
						{status !== "active" && config.is_verified && (
							<Tooltip title="Activate">
								<Button type="text" icon={<CheckOutlined />} size="small" onClick={(e) => { e.stopPropagation(); handleActivate(config); }} />
							</Tooltip>
						)}
						<Tooltip title="Delete">
							<Button type="text" danger icon={<DeleteOutlined />} size="small" onClick={(e) => { e.stopPropagation(); handleDelete(config); }} />
						</Tooltip>
					</Space>
				);
			},
		},
	];

	return (
		<SectionCard
			title="Email Configurations"
			subtitle="Configure SMTP settings for sending emails from your company"
			extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => openDrawer()}>Add Configuration</Button>}
			noPadding
		>
			<DataTable
				columns={columns}
				dataSource={configs}
				rowKey="id"
				loading={loading && !drawerVisible}
				pagination={false}
				emptyTitle="No email configurations"
				emptyDescription="Add an SMTP configuration to start sending emails"
				emptyAction={{ label: "Add Configuration", onClick: () => openDrawer() }}
				bare
			/>

			{/* Configuration Drawer */}
			<DetailDrawer
				title={editingConfig ? "Edit Email Configuration" : "Add Email Configuration"}
				open={drawerVisible}
				onClose={closeDrawer}
				width="70vw"
				headerActions={
					<Button type="primary" icon={<SaveOutlined />} onClick={handleSubmit} loading={loading}>
						{editingConfig ? "Update" : "Create"}
					</Button>
				}
			>
				<Form form={form} layout="vertical">
					{/* SMTP Provider Presets */}
					<div style={{ marginBottom: 16 }}>
						<Text strong style={{ display: "block", marginBottom: 8, fontSize: 12 }}>
							Quick Setup
						</Text>
						<Space size="small" wrap>
							{SMTP_PROVIDERS.map((provider) => (
								<Button key={provider.name} size="small" onClick={() => handleProviderSelect(provider)}>
									{provider.name}
								</Button>
							))}
						</Space>
					</div>

					<Divider style={{ margin: "12px 0" }} />

					<Form.Item label="Configuration Name" name="name" rules={[{ required: true, message: "Required" }]}>
						<Input placeholder="e.g., Company SMTP" />
					</Form.Item>

					<Form.Item label="SMTP Host" name="host" rules={[{ required: true, message: "Required" }]}>
						<Input placeholder="smtp.gmail.com" />
					</Form.Item>

					<Form.Item label="Port" name="port" rules={[{ required: true, message: "Required" }]}>
						<InputNumber min={1} max={65535} style={{ width: "100%" }} />
					</Form.Item>

					<Form.Item label="Encryption" name="encryption" rules={[{ required: true, message: "Required" }]}>
						<Select>
							<Option value="tls">TLS</Option>
							<Option value="ssl">SSL</Option>
						</Select>
					</Form.Item>

					<Form.Item label="Username" name="username" rules={[{ required: true, message: "Required" }]}>
						<Input placeholder="your.email@domain.com" />
					</Form.Item>

					<Form.Item label="Password" name="password" rules={editingConfig ? [] : [{ required: true, message: "Required" }]}>
						<Input.Password placeholder={editingConfig ? "Leave empty to keep current password" : "Your password or app password"} />
					</Form.Item>
					{editingConfig && (
						<Text type="secondary" style={{ fontSize: 11, marginTop: -16, display: "block", marginBottom: 16 }}>
							Leave empty to keep the current password
						</Text>
					)}

					<Form.Item label="From Address" name="from_address" rules={[{ required: true, message: "Required" }]}>
						<Input placeholder="noreply@yourcompany.com" />
					</Form.Item>

					<Form.Item label="From Name" name="from_name" rules={[{ required: true, message: "Required" }]}>
						<Input placeholder="Your Company Name" />
					</Form.Item>
				</Form>
			</DetailDrawer>

			{/* Test Drawer */}
			<DetailDrawer
				title="Test Email Configuration"
				open={testDrawerVisible}
				onClose={() => setTestDrawerVisible(false)}
				width={480}
				headerActions={
					<Button type="primary" onClick={executeTest} loading={testLoading} disabled={!testEmail}>
						Send Test
					</Button>
				}
			>
				<div style={{ marginBottom: 16 }}>
					<Alert message={`Testing configuration: ${testingConfig?.name}`} type="info" showIcon />
				</div>
				<div>
					<Text strong style={{ display: "block", marginBottom: 8, fontSize: 13 }}>
						Test Email Address
					</Text>
					<Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="test@example.com" type="email" />
					<Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 8 }}>
						A test email will be sent to this address
					</Text>
				</div>
			</DetailDrawer>
		</SectionCard>
	);
};

export default CompanyEmailConfiguration;
